/**
 * Context Menu Actions Index
 *
 * Exports all actions and provides the main entry points for:
 * - getContextMenuActions: Returns applicable actions for a datum
 * - handleContextMenuAction: Routes action execution to the appropriate handler
 */

import { Datum, Filetree } from "../datastructure";
import { ContextMenuAction } from "../contextmenu";
import { ActionContext, ActionDefinition, ConfirmationOptions } from "./types";

// Import all actions
import { openFileAction, openFile } from "./openFile";
import { downloadFileAction, downloadFile } from "./downloadFile";
import { downloadFolderAction, downloadFolder } from "./downloadFolder";
import {
  deleteFileAction,
  deleteFolderAction,
  deleteFile,
  deleteFolder,
} from "./deleteFile";
import { renameAction, renameDatum } from "./rename";
import { createFolderAction, createFolder } from "./createFolder";
import {
  cutAction,
  cutDatum,
  getCutItems,
  clearCutItems,
  isDatumCut,
} from "./cut";
import { pasteAction, pasteItems } from "./paste";
import { moveItems } from "./moveItems";
import { syncFolderAction, syncFolder } from "./syncFolder";
import { reprocessAction, reprocessFile } from "./reprocess";
import { sortFolderAction, sortFolder } from "./sortFolder";
import { uploadFilesAction, uploadFolderAction } from "./uploadFiles";
import { uploadFilesToFolder } from "../upload/uploadFiles";
import {
  chatWithDocumentAction,
  chatWithDocumentsAction,
  chatWithDocuments,
  ChatWithDocumentsCallback,
} from "./chatWithDocuments";

// Export individual actions for direct use
export { openFile } from "./openFile";
export { downloadFile } from "./downloadFile";
export { downloadFolder } from "./downloadFolder";
export { deleteFile, deleteFolder, collectFileIds } from "./deleteFile";
export { renameDatum } from "./rename";
export { createFolder } from "./createFolder";
export {
  cutDatum,
  getCutItems,
  clearCutItems,
  isDatumCut,
  onCutItemsChange,
} from "./cut";
export { pasteItems } from "./paste";
export { moveItems } from "./moveItems";
export { syncFolder } from "./syncFolder";
export { reprocessFile } from "./reprocess";
export { sortFolder } from "./sortFolder";
export { uploadFilesToFolder } from "../upload/uploadFiles";
export { chatWithDocuments } from "./chatWithDocuments";
export type { ChatWithDocumentsCallback } from "./chatWithDocuments";

// Export types
export * from "./types";

/**
 * All registered actions (order matters for menu display)
 */
const allActions: ActionDefinition[] = [
  // File actions
  openFileAction,
  chatWithDocumentAction, // Only shown when chat product enabled
  downloadFileAction,
  reprocessAction, // Admin only - filtered by parent

  // Folder actions
  chatWithDocumentsAction, // Only shown when chat product enabled
  downloadFolderAction,
  uploadFilesAction,
  uploadFolderAction,
  createFolderAction,
  syncFolderAction, // Only for synced folders

  // Common actions
  cutAction,
  pasteAction,
  renameAction,
  sortFolderAction, // Admin only - filtered by parent

  // Destructive actions last
  deleteFileAction,
  deleteFolderAction,
];

/**
 * Action handlers mapped by action string
 */
const actionHandlers: Record<
  string,
  (datum: Datum, context: ActionContext) => Promise<void>
> = {
  "open-file": openFile,
  "chat-with-document": chatWithDocuments,
  "chat-with-documents": chatWithDocuments,
  "download-file": downloadFile,
  "download-folder": downloadFolder,
  delete: async (datum, context) => {
    if (datum.type === "file") {
      await deleteFile(datum, context);
    } else {
      await deleteFolder(datum, context);
    }
  },
  rename: async (datum, context) => {
    const newName = window.prompt("Enter new name:", datum.name);
    if (newName) {
      await renameDatum(datum, newName, context);
    }
  },
  "create-folder": async (datum, context) => {
    const folderName = window.prompt("Enter folder name:");
    if (folderName) {
      await createFolder(datum, folderName, context);
    }
  },
  cut: cutDatum,
  paste: pasteItems,
  "sync-folder": syncFolder,
  reprocess: reprocessFile,
  "sort-folder": sortFolder,
  "upload-files": uploadFilesToFolder,
  "upload-folder": uploadFilesToFolder,
};

/**
 * Extended action context with optional callbacks for UI-dependent actions
 */
export interface ExtendedActionContext extends ActionContext {
  /** Whether user is admin (for admin-only actions) */
  isAdmin?: boolean;
  /** Whether multiple items are selected (restricts menu to bulk actions) */
  isMultiSelect?: boolean;
  /** Callback for triggering file upload UI */
  onTriggerUpload?: (
    targetPath: string[],
    driveId: string | null,
    type: "files" | "folder"
  ) => void;
  /** Callback for chat with documents - presence enables the action */
  onChatWithDocuments?: ChatWithDocumentsCallback;
  /** Whether bulk chat with documents is available (determines multi-select label) */
  hasBulkChat?: boolean;
  /**
   * Whether this surface only picks documents rather than acting on them (the
   * file-selection modal). Restricts the menu to PICKER_ACTIONS.
   */
  isPicker?: boolean;
}

/**
 * The only actions offered on a picker surface: inspect a document or download
 * it, nothing that mutates the filesystem or navigates away from the picker.
 *
 * Deliberately an allowlist — a newly registered action stays out of pickers
 * until someone decides it belongs there, which is the safe default for a
 * surface whose job is choosing documents.
 */
const PICKER_ACTIONS: readonly string[] = ["open-file", "download-file"];

/**
 * Returns the list of applicable context menu actions for a datum
 */
export function getContextMenuActions(
  datum: Datum,
  context?: ExtendedActionContext
): ContextMenuAction[] {
  // A picker never takes the bulk branch: that list is hardcoded rather than
  // registry-driven, so it would route straight past PICKER_ACTIONS and offer
  // Cut and Delete on a surface whose whole job is choosing documents.
  if (context?.isMultiSelect && !context?.isPicker) {
    // In multi-select mode, show only bulk-applicable actions
    const actions: ContextMenuAction[] = [
      { label: "Open files", action: "open-files" },
    ];
    if (context?.hasBulkChat) {
      actions.push({
        label: "Chat with documents",
        action: "chat-with-documents",
      });
    }
    actions.push(
      { label: "Cut", action: "cut" },
      { label: "Delete", action: "delete" }
    );
    return actions;
  }

  const actions: ContextMenuAction[] = [];
  const isAdmin = context?.isAdmin ?? false;

  for (const actionDef of allActions) {
    // Skip admin-only actions if not admin
    if (actionDef.config.action === "reprocess" && !isAdmin) continue;
    if (actionDef.config.action === "sort-folder" && !isAdmin) continue;
    if (context?.isPicker && !PICKER_ACTIONS.includes(actionDef.config.action))
      continue;

    if (actionDef.isApplicable(datum, context)) {
      actions.push({
        label: actionDef.config.label,
        action: actionDef.config.action,
        disabled: actionDef.config.disabled,
        separator: actionDef.config.separator,
      });
    }
  }

  // Organize actions with separators
  return organizeActionsWithSeparators(datum, actions);
}

/**
 * Organize actions with logical separators
 */
function organizeActionsWithSeparators(
  datum: Datum,
  actions: ContextMenuAction[]
): ContextMenuAction[] {
  const organizedActions: ContextMenuAction[] = [];

  if (datum.type === "file") {
    // File actions order: Open, Chat, Download | Reprocess, Sync | Cut, Rename | Delete
    const groups = [
      ["open-file", "chat-with-document", "download-file"],
      ["reprocess", "sync-folder"],
      ["cut", "rename"],
      ["delete"],
    ];

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const groupActions = group
        .map((action) => actions.find((a) => a.action === action))
        .filter(Boolean) as ContextMenuAction[];

      if (groupActions.length > 0) {
        if (organizedActions.length > 0) {
          organizedActions.push({ label: "", action: "", separator: true });
        }
        organizedActions.push(...groupActions);
      }
    }
  } else {
    // Folder actions order: Chat, Upload, Create | Download, Sync | Cut, Paste, Rename, Sort | Delete
    const groups = [
      ["chat-with-documents", "upload-files", "upload-folder", "create-folder"],
      ["download-folder", "sync-folder"],
      ["cut", "paste", "rename", "sort-folder"],
      ["delete"],
    ];

    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      const groupActions = group
        .map((action) => actions.find((a) => a.action === action))
        .filter(Boolean) as ContextMenuAction[];

      if (groupActions.length > 0) {
        if (organizedActions.length > 0) {
          organizedActions.push({ label: "", action: "", separator: true });
        }
        organizedActions.push(...groupActions);
      }
    }
  }

  return organizedActions;
}

/**
 * Handles a context menu action
 */
export async function handleContextMenuAction(
  action: string,
  datum: Datum,
  context: ActionContext
): Promise<void> {
  const handler = actionHandlers[action];

  if (!handler) {
    console.warn(`Unknown action: ${action}`);
    return;
  }

  try {
    await handler(datum, context);
  } catch (error) {
    console.error(`Action ${action} failed:`, error);
    context.showToast?.(`Action failed: ${error}`, "error");
  }
}

/**
 * Create a default action context
 */
export function createActionContext(options: {
  refreshTree: () => void;
  driveId?: string;
  orgId?: string;
  readOnlyDriveNames?: readonly string[];
  sftpDriveNames?: readonly string[];
  sharepointRootNames?: readonly string[];
  isAdmin?: boolean;
  filetree?: Filetree;
  isAcrossDrivesMode?: boolean;
  isPicker?: boolean;
  showConfirmation?: (options: ConfirmationOptions) => Promise<boolean>;
  onSortStarted?: (taskId: string, total: number) => void;
}): ExtendedActionContext {
  return {
    refreshTree: options.refreshTree,
    driveId: options.driveId,
    orgId: options.orgId,
    readOnlyDriveNames: options.readOnlyDriveNames,
    sftpDriveNames: options.sftpDriveNames,
    sharepointRootNames: options.sharepointRootNames,
    isAdmin: options.isAdmin,
    isAcrossDrivesMode: options.isAcrossDrivesMode,
    isPicker: options.isPicker,
    filetree: options.filetree,
    showConfirmation: options.showConfirmation,
    onSortStarted: options.onSortStarted,
    showToast: (message, type) => {
      // Use the global toast if available
      if (typeof window !== "undefined" && (window as any).openFpToast) {
        (window as any).openFpToast(message, type);
      }
    },
  };
}
